import Tooltip from "@/v2-ui/tooltip"
import { DotText, type DotProps } from "@/v2-backoffice-client/components/dot"
import { PROD_STATE } from "@/v2-backoffice-client/layer/authoring/layer.authoring.constants"
import type { LayerAuthoringStatus } from "@/v2-backoffice-client/layer/authoring/layer.authoring.status.query"

export type LayerProdStateDotProps = {
  className?: string
  /** undefined = the layer has no catalog row (runtime/AGOL only). */
  status: LayerAuthoringStatus | undefined
}

type ProdStateView = {
  variant: DotProps["variant"]
  label: string
  explanation: string
}

/**
 * The production chip, in plain words. Layers are authored here (the dev
 * catalog) and copied to the production catalog with "Push prod"; the chip
 * says whether production has this layer's current definition, and what
 * to do if not. It used to say "In prod" / "Prod outdated" / "Dev only",
 * which told an operator nothing about what had happened or what to press.
 */
function getProdStateView(status: LayerAuthoringStatus): ProdStateView {
  if (status.IsDeleted) {
    return {
      variant: "destructive",
      label: "Deleting",
      explanation:
        "Deleted from the catalog. The next catalog publish withdraws the layer from the app; Restore in the toolbar undoes it until then.",
    }
  }
  if (status.ProdState === PROD_STATE.inProd) {
    return {
      variant: "success-1",
      label: "Up to date",
      explanation:
        "Production's catalog has this layer's current definition. It reaches the production app with the next \"Publish to prod\" run (and, for a new layer, a publish from prod's review queue).",
    }
  }
  if (status.ProdState === PROD_STATE.outdated) {
    return {
      variant: "warning",
      label: "Push needed",
      explanation:
        'The layer changed here after it was last pushed, so production\'s catalog still has the older definition. Push prod copies the current one; it goes live with the next "Publish to prod" run.',
    }
  }
  return {
    variant: "muted",
    label: "Never pushed",
    explanation:
      "This layer exists in the dev catalog only. Push prod copies its definition to production, where it then waits in prod's review queue until someone publishes it.",
  }
}

function LayerProdStateDot(props: LayerProdStateDotProps) {
  const { status, className } = props
  if (!status) return null

  const view = getProdStateView(status)

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <span className="inline-flex cursor-help">
          <DotText className={className} variant={view.variant}>
            {view.label}
          </DotText>
        </span>
      </Tooltip.Trigger>
      <Tooltip.Content side="bottom">
        <span className="block max-w-xs">{view.explanation}</span>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default LayerProdStateDot
